import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { X, GraduationCap, CheckCircle2, User } from "lucide-react";
import { IMAGES, COURSE_TOPICS, COURSES } from "../../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const YellowMark = ({ children }) => (
  <div className="flex items-center gap-3 mb-4">
    <span className="h-6 w-1.5 bg-oyf-yellow rounded-full" />
    <h3 className="font-display font-extrabold text-oyf-black text-xl md:text-2xl">{children}</h3>
  </div>
);

const InterestForm = () => {
  const [form, setForm] = useState({ full_name: "", telephone: "", email: "", experience_level: "", course_interest: "", learning_format: "", consent: false });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error("Please provide consent to continue."); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/course-interest`, form);
      setDone(true);
      toast.success(res.data.message);
    } catch {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally { setLoading(false); }
  };

  if (done) return (
    <div className="bg-oyf-grey rounded-lg p-8 text-center" data-testid="course-interest-success">
      <CheckCircle2 className="mx-auto text-oyf-yellow" size={40} />
      <p className="mt-3 font-display font-bold text-oyf-black text-lg">Thank you for your interest. Course announcements will be shared when registration becomes available.</p>
    </div>
  );

  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="course-interest-form">
      <div className="sm:col-span-2"><Label className="text-oyf-black font-semibold">Full name</Label><Input required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="mt-1.5 text-base" data-testid="course-name" /></div>
      <div><Label className="text-oyf-black font-semibold">Telephone number</Label><Input required value={form.telephone} onChange={(e) => set("telephone", e.target.value)} className="mt-1.5 text-base" data-testid="course-phone" /></div>
      <div><Label className="text-oyf-black font-semibold">Email address</Label><Input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5 text-base" data-testid="course-email" /></div>
      <div>
        <Label className="text-oyf-black font-semibold">Current experience level</Label>
        <Select value={form.experience_level} onValueChange={(v) => set("experience_level", v)}>
          <SelectTrigger className="mt-1.5 text-base" data-testid="course-experience"><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="None">None / New</SelectItem>
            <SelectItem value="Some">Some Experience</SelectItem>
            <SelectItem value="Experienced">Experienced</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-oyf-black font-semibold">Primary course interest</Label>
        <Select value={form.course_interest} onValueChange={(v) => set("course_interest", v)}>
          <SelectTrigger className="mt-1.5 text-base" data-testid="course-interest"><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>
            {COURSES.map((c) => <SelectItem key={c.key} value={c.t}>{c.t}</SelectItem>)}
            <SelectItem value="All Courses">All Courses</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="sm:col-span-2">
        <Label className="text-oyf-black font-semibold">Preferred learning format</Label>
        <Select value={form.learning_format} onValueChange={(v) => set("learning_format", v)}>
          <SelectTrigger className="mt-1.5 text-base" data-testid="course-format"><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Online">Online</SelectItem>
            <SelectItem value="In Person">In Person</SelectItem>
            <SelectItem value="Either">Either</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <label className="sm:col-span-2 flex items-start gap-3 cursor-pointer">
        <Checkbox checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} className="mt-0.5" data-testid="course-consent" />
        <span className="text-sm text-oyf-medgrey">I consent to being contacted by One York Freight regarding this course.</span>
      </label>
      <button type="submit" disabled={loading} className="sm:col-span-2 bg-oyf-yellow text-oyf-black font-bold py-4 rounded-md hover:-translate-y-0.5 hover:shadow-[4px_4px_0_0_#202124] transition-transform duration-200 disabled:opacity-60" data-testid="course-submit">
        {loading ? "Submitting..." : "Join the Course Interest List"}
      </button>
    </form>
  );
};

export const CourseBanner = () => {
  const [open, setOpen] = useState(false);
  return (
    <section id="training" className="bg-white py-14 md:py-20" data-testid="course-banner">
      <div className="container-oyf">
        <div className="relative overflow-hidden rounded-2xl bg-oyf-charcoal course-glow border border-oyf-yellow/20">
          <div className="relative z-10 p-7 md:p-10">
            {/* Header row: Yulia top-left */}
            <div className="flex items-center gap-4">
              <motion.div
                initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
                className="w-16 h-16 md:w-20 md:h-20 rounded-full overflow-hidden border-2 border-oyf-yellow shrink-0"
              >
                <img src={IMAGES.yulia} alt="Yulia Kalatcheva, Dispatch Operations Specialist and Course Trainer" className="w-full h-full object-cover" />
              </motion.div>
              <div>
                <span className="pulse-badge inline-flex items-center gap-2 bg-oyf-yellow text-oyf-black text-[11px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-full">
                  <GraduationCap size={13} /> Coming Soon
                </span>
                <p className="mt-1.5 text-white font-display font-bold text-sm">Led by Yulia Kalatcheva</p>
                <p className="text-xs text-oyf-yellow/90">Dispatch Operations Specialist, Trainer & Company Owner</p>
              </div>
            </div>

            <h2 className="mt-6 font-display font-extrabold text-white tracking-tight text-2xl md:text-4xl leading-[1.05]">
              Build, Launch and Operate a Trucking Business
            </h2>
            <p className="mt-3 text-white/65 text-sm md:text-base leading-relaxed max-w-2xl">
              A practical public training program. Choose from three focused courses covering the essential systems behind a professional transportation company.
            </p>

            <div className="mt-5 flex flex-wrap gap-2">
              {COURSE_TOPICS.map((t, i) => (
                <motion.span key={t}
                  initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                  className="text-xs font-semibold text-white/85 bg-white/5 border border-white/10 px-3 py-1.5 rounded-full"
                >{t}</motion.span>
              ))}
            </div>

            <button onClick={() => setOpen(true)} className="mt-6 inline-flex items-center gap-2 bg-oyf-yellow text-oyf-black font-bold px-6 py-3.5 rounded-md hover:-translate-y-0.5 hover:shadow-[5px_5px_0_0_#F4C400] transition-transform duration-200" data-testid="course-view-btn">
              View Course Information
            </button>
          </div>
        </div>
      </div>

      {/* Course Modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[960px] w-[95vw] max-h-[85vh] overflow-y-auto bg-white p-0 gap-0" data-testid="course-modal">
          <DialogTitle className="sr-only">Dispatch and Trucking Business Operations Course</DialogTitle>
          <div className="sticky top-0 z-20 bg-oyf-charcoal px-6 md:px-8 py-5 flex items-center justify-between">
            <div>
              <p className="text-oyf-yellow text-xs font-bold tracking-widest uppercase">Training Program</p>
              <h2 className="font-display font-extrabold text-white text-xl md:text-2xl mt-0.5">Dispatch and Trucking Business Operations Course</h2>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white p-2 shrink-0" aria-label="Close" data-testid="course-modal-close"><X size={24} /></button>
          </div>

          <div className="px-6 md:px-8 py-8 space-y-10">
            <p className="text-oyf-medgrey text-base leading-relaxed">
              Learn the operational foundations required to understand dispatching and establish a transportation business. This upcoming public program is offered as three focused courses explaining the systems, documents, responsibilities and professional skills involved in the trucking and expedited-freight industry.
            </p>

            {COURSES.map((c) => (
              <div key={c.key}>
                <YellowMark>{c.t}</YellowMark>
                <p className="text-oyf-medgrey leading-relaxed">{c.summary}</p>
                <ul className="mt-4 grid sm:grid-cols-2 gap-2">
                  {c.items.map((it) => (<li key={it} className="flex gap-2.5 text-sm text-oyf-medgrey"><CheckCircle2 size={16} className="text-oyf-yellow shrink-0 mt-0.5" />{it}</li>))}
                </ul>
                {c.note && <p className="mt-3 text-xs text-oyf-charcoal bg-oyf-yellow/15 border border-oyf-yellow/40 rounded p-3 leading-relaxed">{c.note}</p>}
              </div>
            ))}

            <div>
              <YellowMark>Meet Yulia Kalatcheva</YellowMark>
              <div className="flex flex-col sm:flex-row gap-5 items-start">
                <img src={IMAGES.yulia} alt="Yulia Kalatcheva" className="w-24 h-24 rounded-full object-cover border-2 border-oyf-yellow shrink-0" />
                <p className="text-oyf-medgrey leading-relaxed">Yulia Kalatcheva is an experienced Dispatch Operations Specialist, course trainer and company owner. Her instruction focuses on practical transportation workflows, professional communication, business organization and the operational knowledge required to navigate the dispatch and freight industry.</p>
              </div>
            </div>

            <div className="bg-oyf-charcoal rounded-lg p-6 text-white">
              <div className="flex items-center gap-3 mb-2">
                <span className="h-6 w-1.5 bg-oyf-yellow rounded-full" />
                <h3 className="font-display font-extrabold text-xl">Registration Opening Soon</h3>
              </div>
              <p className="text-white/70 leading-relaxed">Course dates, delivery format, duration, tuition and registration details will be announced soon.</p>
            </div>

            <div>
              <YellowMark><span className="inline-flex items-center gap-2"><User size={20} className="text-oyf-yellow" /> Join the Course Interest List</span></YellowMark>
              <InterestForm />
            </div>

            <p className="text-xs text-oyf-medgrey border-t border-oyf-grey pt-5 leading-relaxed">
              This course provides operational education and does not replace legal, customs, accounting, insurance or regulatory advice. Requirements vary according to jurisdiction, vehicle type, weight, cargo, route and business model.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};
